import FolderPicker from './FolderPicker';
import API_URL from './api';
import { useState, useEffect } from 'react';
import './App.css';

function AllPins({ userData }) {
    const [pins, setPins] = useState([]);
    const [folders, setFolders] = useState([]);
    const [movingPinId, setMovingPinId] = useState(null);
    const [selectedPins, setSelectedPins] = useState([]);
    const [bulkFolderId, setBulkFolderId] = useState('');
    const [expandedPinId, setExpandedPinId] = useState(null);

    useEffect(() => {
        fetchAllPins();
        fetchFolders();
    }, []);

    async function fetchAllPins() {
        try {
            const res = await fetch(`${API_URL}/api/pins`, {
                headers: { Authorization: 'Bearer ' + userData.token },
            });
            const data = await res.json();
            setPins(data.pins || []);
        } catch (err) {
            console.error('Could not fetch pins:', err);
        }
    }

    async function fetchFolders() {
        try {
            const res = await fetch(`${API_URL}/api/folders`, {
                headers: { Authorization: 'Bearer ' + userData.token },
            });
            const data = await res.json();
            setFolders(data.folders || []);
        } catch (err) {
            console.error('Could not fetch folders:', err);
        }
    }

    async function deletePin(pinId) {
        if (!window.confirm('Delete this pin? This cannot be undone.')) return;
        try {
            const res = await fetch(`${API_URL}/api/pins/${pinId}`, {
                method: 'DELETE',
                headers: { Authorization: 'Bearer ' + userData.token },
            });
            if (res.ok) {
                await fetchAllPins();
            } else {
                console.error('Delete failed:', res.status);
            }
        } catch (err) {
            console.error('Could not delete pin:', err);
        }
    }

    async function movePin(pinId, folderId) {
        try {
            const res = await fetch(`${API_URL}/api/pins/${pinId}/folder`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + userData.token,
                },
                body: JSON.stringify({ folderId: folderId === 'inbox' ? null : folderId }),
            });
            console.log('movePin response:', res.status);
            setMovingPinId(null);
            await fetchAllPins();
        } catch (err) {
            console.error('Could not move pin:', err);
        }
    }

    async function bulkMove() {
        if (!bulkFolderId || selectedPins.length === 0) return;
        if (bulkFolderId === 'new') {
            const name = window.prompt('New chapter name:');
            if (!name) return;
            try {
                const res = await fetch(`${API_URL}/api/folders`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name, systemPrompt: '', token: userData.token }),
                });
                const data = await res.json();
                if (res.ok) {
                    await Promise.all(selectedPins.map(pinId =>
                        fetch(`${API_URL}/api/pins/${pinId}/folder`, {
                            method: 'PATCH',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: 'Bearer ' + userData.token,
                            },
                            body: JSON.stringify({ folderId: data.folder.id }),
                        })
                    ));
                    setSelectedPins([]);
                    setBulkFolderId('');
                    fetchFolders();
                    fetchAllPins();
                }
            } catch (err) {
                console.error('Could not create folder:', err);
            }
            return;
        }
        await Promise.all(selectedPins.map(pinId =>
            fetch(`${API_URL}/api/pins/${pinId}/folder`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + userData.token,
                },
                body: JSON.stringify({ folderId: bulkFolderId === 'inbox' ? null : bulkFolderId }),
            })
        ));
        setSelectedPins([]);
        setBulkFolderId('');
        fetchAllPins();
    }

    function toggleSelect(pinId) {
        setSelectedPins(prev =>
            prev.includes(pinId) ? prev.filter(id => id !== pinId) : [...prev, pinId]
        );
    }

    function getFolderName(folderId) {
        if (!folderId) return 'Inbox';
        const folder = folders.find(f => f.id === folderId);
        return folder ? folder.name : 'Unknown';
    }

    return (
        <div className="mypins-view">

            {/* Header row */}
            <div className="allpins-header-row">
                <div className="select-all-row">
                    <input
                        type="checkbox"
                        id="allpins-select-all"
                        className="pin-checkbox"
                        style={{ marginLeft: '0px' }}
                        checked={selectedPins.length === pins.length && pins.length > 0}
                        onChange={(e) => {
                            if (e.target.checked) {
                                setSelectedPins(pins.map(p => p.id));
                                setBulkFolderId('');
                            } else {
                                setSelectedPins([]);
                                setBulkFolderId('');
                            }
                        }}
                    />
                    <label htmlFor="allpins-select-all" className="select-all-label">
                        {selectedPins.length === pins.length && pins.length > 0 ? 'Deselect All' : 'Select All'}
                    </label>
                </div>
                <h2>All Pins ({pins.length})</h2>
                <div></div>
            </div>

            {/* Bulk move */}
            {selectedPins.length > 0 && (
                <div className="bulk-move">
                    <span>{selectedPins.length} selected</span>
                    <select
                        value={bulkFolderId}
                        onChange={(e) => setBulkFolderId(e.target.value)}
                    >
                        <option value="" disabled>Change to...</option>
                        {folders.map(f => (
                            <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                        <option value="inbox">Inbox</option>
                        <option value="new">+ Create new chapter...</option>
                    </select>
                    <button className="move-btn" onClick={bulkMove}>Change Chapter</button>
                    <button className="cancel-btn" onClick={() => setSelectedPins([])}>Cancel</button>
                </div>
            )}

            {pins.length === 0 && (
                <p className="empty">No pins yet. Email a URL to your pinbook address to get started.</p>
            )}

            {pins.map((pin) => (
                <div
                    className={`allpins-card ${selectedPins.includes(pin.id) ? 'selected' : ''}`}
                    key={pin.id}
                    onClick={() => setExpandedPinId(expandedPinId === pin.id ? null : pin.id)}
                    style={{ cursor: 'pointer' }}
                >
                    {/* Top line — checkbox, title, folder tag */}
                    <div className="allpins-top">
                        <input
                            type="checkbox"
                            className="pin-checkbox"
                            checked={selectedPins.includes(pin.id)}
                            onChange={() => toggleSelect(pin.id)}
                            onClick={(e) => e.stopPropagation()}
                        />
                        <a href={pin.url} target="_blank" rel="noreferrer" className="allpins-title-link" onClick={(e) => e.stopPropagation()}>{pin.title || pin.url}</a>
                        <span className="tag">📁 {getFolderName(pin.folder_id)}</span>
                    </div>

                    {/* Middle line — transcript excerpt */}
                    <p className="allpins-excerpt">{pin.transcript}</p>

                    {/* Expanded details */}
                    {expandedPinId === pin.id && (
                        <div className="pin-expanded" onClick={(e) => e.stopPropagation()}>
                            {pin.description && <p className="pin-expanded-desc">{pin.description}</p>}
                            <p className="pin-expanded-transcript">{pin.transcript}</p>
                            <p className="pin-platform">{pin.platform}</p>
                            <p className="pin-date">{new Date(pin.saved_at).toLocaleDateString()} {new Date(pin.saved_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                            <a href={pin.url} target="_blank" rel="noreferrer" className="allpins-url" style={{ display: 'block', marginTop: '6px' }}>{pin.url}</a>
                        </div>
                    )}

                    {/* Bottom line — change folder button and date */}
                    <div className="allpins-bottom" onClick={(e) => e.stopPropagation()}>
                        {movingPinId === pin.id ? (
                            <FolderPicker
                                pinId={pin.id}
                                folders={folders}
                                userData={userData}
                                onMove={(pinId, folderId) => movePin(pinId, folderId)}
                                onCancel={() => setMovingPinId(null)}
                                onFolderCreated={fetchFolders}
                            />
                        ) : (
                            <>
                                <button className="move-btn" onClick={() => setMovingPinId(pin.id)}>Change Chapter</button>
                                <button className="delete-btn" onClick={() => deletePin(pin.id)}><span className="trash-icon">🗑️</span></button>
                                <span className="pin-date">{new Date(pin.saved_at).toLocaleDateString()} {new Date(pin.saved_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default AllPins;